import {useCallback} from "react";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";
import {useUser} from "@/components/Providers/JWTProvider.tsx";
import {useStripeTerminal} from "@/components/Providers/StripeTerminalProvider.tsx";
import {
    buildInPersonPaymentResult,
    type InPersonPaymentOutcome,
} from "@/Helpers/buildInPersonPaymentResult.ts";

type ScriptResult = {
    message: string;
    errorCode: number;
    data: unknown;
};

const useCashPayment = () => {
    const {user} = useUser();
    const {cart, refreshCart, cartPaymentSuccess} = useCartContext();
    const {getConnectedOrPreferredReaderId} = useStripeTerminal();

    return useCallback(
        async (tenderedCents: number, payNowAmount: number): Promise<InPersonPaymentOutcome> => {
            if (!(user && window.FileMaker && cart)) {
                console.error("invalid context", !(user && window.FileMaker && cart));
                return {error: "An error occurred (-1)"};
            }

            try {
                const fmCallBack = new Promise<string>(resolve => {
                    let resolved = false;
                    window.FileMakerCallback = (response: string) => {
                        resolved = true;
                        resolve(`${response}`);
                    };
                    setTimeout(() => {
                        if (!resolved) {
                            resolve(
                                JSON.stringify({
                                    message: "FileMaker Call timed out",
                                    errorCode: 100,
                                    data: null,
                                }),
                            );
                        }
                    }, 10_000); //wait 10 seconds
                });
                window.FileMaker.PerformScript(
                    "patchCartTenderedCents",
                    JSON.stringify({
                        cartId: cart?.id,
                        tenderedCents: tenderedCents,
                    }),
                );

                const fmResponse = JSON.parse(await fmCallBack) as ScriptResult;
                if (fmResponse.errorCode !== 0) {
                    console.error("fmResponse", fmResponse);
                    return {
                        error: fmResponse.message
                            ? `${fmResponse.message}`
                            : "There was an error setting the tendered cash",
                    };
                }

                const terminalId =
                    getConnectedOrPreferredReaderId() ?? localStorage.getItem("PreferredReaderId");

                const url = new URL(
                    "/v1/payment/complete-in-person-transaction",
                    import.meta.env.VITE_APP_API_ENDPOINT,
                );
                const response = await user.apiFetch(url, {
                    method: "POST",
                    body: JSON.stringify({
                        cartId: cart.id,
                        payNowAmount,
                        terminalId,
                    }),
                });

                const responseBody = await response.json().catch(() => null);

                if (response.ok) {
                    const result = buildInPersonPaymentResult(
                        "cash",
                        payNowAmount,
                        responseBody,
                        tenderedCents,
                    );
                    await cartPaymentSuccess(result.message, result.alertVariant);
                    await refreshCart();
                    return result.needsAcknowledgement ? {acknowledgement: result.message} : {};
                }

                if (
                    responseBody &&
                    "hint" in responseBody &&
                    typeof responseBody.hint === "string"
                ) {
                    return {error: responseBody.hint};
                }
            } catch (e) {
                console.error("error in useCashPayment", e);
            }
            return {error: "There was an error."};
        },
        [user, cart],
    );
};

export default useCashPayment;
